import { PaymentProcessorEnum } from '@systemeio/ui'
import { ContactSubscriptionsStatusEnum } from 'modules/contacts/contact-page/enums/contact-subscriptions-enum'
import { ContactSubscriptionItemInterface } from 'modules/contacts/contact-page/types/contact-subscriptions-interface'
import { UnsubscribeOptions } from 'modules/payment/subscriptions/hooks/use-unsubscribe'
import { PaystackPaymentProcessor } from 'modules/profile/payment-gateways/integrations/paystack/types'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import { PaymentItemInterface } from 'shared/types/payment-interface'
import { SUBSCRIPTIONS_API } from '../api/subscriptions-api'
import { GocardlessCancelBodyInterface } from '../types/gocardless-cancel-body'
import { SubscriptionId, SubscriptionInterface } from '../types/subscription-interface'
import { checkIsContactsSubscription } from './check-is-contact-subscription'

const getGocardlessCancelSubscriptionUrl = () => () =>
  `/api/payment/integration/gocardless/subscription/cancel`

const getGocardlessCancelSubscriptionBody =
  (subscriptionId: SubscriptionId) =>
  (time: 'now' | 'when_ends'): GocardlessCancelBodyInterface => ({
    subscription: subscriptionId,
    cancelledManually: true,
    cancelledImmediately: time === 'now',
  })

const getXenditCancelSubscriptionUrl = (subscriptionId: SubscriptionId) => {
  return (time: 'now' | 'when_ends') =>
    `/api/payment/integration/xendit/subscription/cancel/${subscriptionId}${
      time === 'now' ? '/immediately' : ''
    }`
}

const getPaystackCancelSubscriptionUrl = (
  subscriptionId: SubscriptionId,
  paymentProcessorName: PaystackPaymentProcessor,
) => {
  return (time: 'now' | 'when_ends') =>
    `/api/payment/integration/${paymentProcessorName}/subscription/cancel/${subscriptionId}${
      time === 'now' ? '/immediately' : ''
    }`
}

const getDefaultCancelSubscriptionUrl = (subscriptionId: PaymentItemInterface['id']) => {
  return (time: 'now' | 'when_ends') =>
    `${SUBSCRIPTIONS_API}/${
      time === 'now' ? 'unsubscribe' : 'unsubscribe-when-ends'
    }/${subscriptionId}`
}

const getSnPCancelSubscriptionUrl = (subscriptionId: PaymentItemInterface['id']) => {
  return (time: 'now' | 'when_ends') =>
    `/api/payment/integration/secure-and-pay/subscription/cancel/${subscriptionId}${
      time === 'now' ? '/immediately' : ''
    }`
}

const getPaypalRestCancelSubscriptionUrl = (subscriptionId: SubscriptionId) => {
  return (time: 'now' | 'when_ends') =>
    `/api/payment/integration/paypal-rest/subscription/cancel/${subscriptionId}${
      time === 'now' ? '/immediately' : ''
    }`
}

export const getPaymentProcessorUnsubscribeOptions = (
  paymentProcessor: PaymentProcessorEnum,
  subscriptionId: SubscriptionId,
  subscriptionItems:
    | ContactSubscriptionItemInterface[]
    | SubscriptionInterface['subscriptionItems'],
): UnsubscribeOptions => {
  switch (paymentProcessor) {
    case PaymentProcessorEnum.Gocardless: {
      const requestMethod = RequestMethodsEnum.post

      return {
        requestMethod,
        getRequestUrl: getGocardlessCancelSubscriptionUrl(),
        getRequestBody: getGocardlessCancelSubscriptionBody(subscriptionId),
      }
    }
    case PaymentProcessorEnum.XenditID:
    case PaymentProcessorEnum.XenditPH: {
      const requestMethod = RequestMethodsEnum.delete

      return { requestMethod, getRequestUrl: getXenditCancelSubscriptionUrl(subscriptionId) }
    }
    case PaymentProcessorEnum.PaystackCI:
    case PaymentProcessorEnum.PaystackKE:
    case PaymentProcessorEnum.PaystackEG:
    case PaymentProcessorEnum.PaystackZA:
    case PaymentProcessorEnum.PaystackGH:
    case PaymentProcessorEnum.PaystackNG: {
      const requestMethod = RequestMethodsEnum.delete

      return {
        requestMethod,
        getRequestUrl: getPaystackCancelSubscriptionUrl(subscriptionId, paymentProcessor),
      }
    }
    case PaymentProcessorEnum.SecureAndPay: {
      const requestMethod = RequestMethodsEnum.delete

      return { requestMethod, getRequestUrl: getSnPCancelSubscriptionUrl(subscriptionId) }
    }
    case PaymentProcessorEnum.PaypalRest: {
      const requestMethod = RequestMethodsEnum.delete

      return { requestMethod, getRequestUrl: getPaypalRestCancelSubscriptionUrl(subscriptionId) }
    }
    default: {
      const requestMethod = RequestMethodsEnum.post
      const id = checkIsContactsSubscription(subscriptionItems)
        ? (
            subscriptionItems.find(
              item => item?.status === ContactSubscriptionsStatusEnum.active,
            ) as ContactSubscriptionItemInterface
          ).id
        : subscriptionItems[0].id

      return { requestMethod, getRequestUrl: getDefaultCancelSubscriptionUrl(id) }
    }
  }
}
